import { useEffect, useState } from 'react';
import { PhoneIcon, PhoneXMarkIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useSip } from '../SipContext';
import { PointMascot } from '../PointMascot/PointMascot';

const STATUS_COLORS = {
    connected: 'bg-emerald-500',
    connecting: 'bg-amber-500',
    disconnected: 'bg-red-500',
};

function formatDuration(seconds) {
    const m = String(Math.floor(seconds / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return `${m}:${s}`;
}

function getCallView(callState) {
    if (callState === 'active') {
        return {
            label: 'En llamada',
            caption: 'Conversacion activa',
            mascotPose: 'peek',
            mascotClass: 'sip-mascot-on-call',
            ringClass: 'border-emerald-300 shadow-emerald-200/70',
        };
    }

    if (callState === 'ringing') {
        return {
            label: 'Timbrando',
            caption: 'Esperando respuesta',
            mascotPose: 'peek',
            mascotClass: 'sip-mascot-ringing',
            ringClass: 'border-amber-300 shadow-amber-200/70',
        };
    }

    return {
        label: 'Listo',
        caption: 'Disponible para llamar',
        mascotPose: 'rest',
        mascotClass: 'sip-mascot-ready',
        ringClass: 'border-slate-200 shadow-slate-200/70',
    };
}

export default function PhonePanel() {
    const {
        status,
        statusText,
        extension,
        callState,
        callSeconds,
        callTarget,
        callClientName,
        dialerRequest,
        makeCall,
        hangup,
        trackingId
    } = useSip();
    const [open, setOpen] = useState(false);
    const [showDialPad, setShowDialPad] = useState(false);
    const [dialNumber, setDialNumber] = useState('');
    const [dialContext, setDialContext] = useState(null);

    const isRegistered = extension !== null;
    const isInCall = callState !== 'idle';
    const view = getCallView(callState);
    const displayNumber = showDialPad && !isInCall ? dialNumber : callTarget;

    const appendDigit = (digit) => {
        setDialNumber((value) => `${value}${digit}`.slice(0, 10));
    };

    const removeDigit = () => {
        setDialNumber((value) => value.slice(0, -1));
    };

    const handleNewCall = () => {
        setOpen(true);
        setShowDialPad(true);
        setDialNumber(dialContext?.lastNumber || '');
    };

    const handleDial = () => {
        const target = dialNumber.trim();
        if (!target) return;

        // if (dialContext?.trackingId) {
        //     console.log('Llamada desde DetalleCobranza:', {
        //         numero: target,
        //         idCompra: dialContext.trackingId,
        //         cliente: dialContext.clientName,
        //         trackingIdEnviado: dialContext.trackingId,
        //     });
        // }

        makeCall(target, dialContext?.trackingId ?? null, {
            clientName: dialContext?.clientName ?? null,
        });
        setShowDialPad(false);
    };

    useEffect(() => {
        if (!dialerRequest) return;

        setOpen(true);
        setShowDialPad(true);
        setDialNumber(dialerRequest.target || '');
        setDialContext({
            trackingId: dialerRequest.trackingId,
            clientName: dialerRequest.clientName,
            lastNumber: dialerRequest.target || ''
        });
    }, [dialerRequest]);

    useEffect(() => {
        if (callClientName) {
            setDialContext(prev => ({
                ...prev,
                clientName: callClientName,
                trackingId: trackingId,
                lastNumber: callTarget
            }));
        }
    }, [callClientName, trackingId, callTarget]);

    useEffect(() => {
        if (isInCall) {
            setOpen(true);
            setShowDialPad(false);
        }
    }, [isInCall]);

    useEffect(() => {
        if (!open || !showDialPad || isInCall) return;

        const handleKeyDown = (event) => {
            if (/^\d$/.test(event.key)) {
                event.preventDefault();
                appendDigit(event.key);
                return;
            }

            if (event.key === 'Backspace') {
                event.preventDefault();
                removeDigit();
                return;
            }

            if (event.key === 'Enter') {
                event.preventDefault();
                handleDial();
                return;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                setShowDialPad(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [dialContext, dialNumber, isInCall, open, showDialPad]);

    return (
        <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-2">
            <style>{`
                .sip-phone-shell {
                    background:
                        radial-gradient(circle at 25% 18%, rgba(255, 215, 0, 0.18), transparent 28%),
                        radial-gradient(circle at 82% 12%, rgba(235, 42, 27, 0.18), transparent 24%),
                        linear-gradient(160deg, #111827 0%, #172554 48%, #0f172a 100%);
                }
                .sip-screen-glow {
                    background:
                        linear-gradient(180deg, rgba(255,255,255,0.96), rgba(248,250,252,0.92)),
                        radial-gradient(circle at center, rgba(45,54,137,0.12), transparent 60%);
                }
                .sip-mascot-wrap .mascota svg .pierna,
                .sip-mascot-wrap .mascota svg .brazo {
                    transform-box: fill-box;
                }
                .sip-mascot-wrap .mascota svg .pierna-i { transform-origin: 60% 0%; }
                .sip-mascot-wrap .mascota svg .pierna-d { transform-origin: 40% 0%; }
                .sip-mascot-wrap .mascota svg .brazo-i  { transform-origin: 95% 95%; }
                .sip-mascot-wrap .mascota svg .brazo-d  { transform-origin: 5% 95%; }
                @keyframes sipRingingLean {
                    0%, 100% { transform: translateX(0) rotate(-1deg); }
                    25% { transform: translateX(-4px) rotate(-4deg); }
                    75% { transform: translateX(4px) rotate(3deg); }
                }
                @keyframes sipTalkBob {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-4px); }
                }
                @keyframes sipArmTalkLeft {
                    0%, 100% { transform: rotate(22deg) translateY(-2px); }
                    50% { transform: rotate(32deg) translateY(-8px); }
                }
                @keyframes sipArmTalkRight {
                    0%, 100% { transform: rotate(-28deg) translateY(-10px); }
                    50% { transform: rotate(-38deg) translateY(-14px); }
                }
                @keyframes sipPulseRing {
                    0%, 100% { transform: scale(1); opacity: .55; }
                    50% { transform: scale(1.08); opacity: .95; }
                }
                @keyframes sipPhoneFloat {
                    0%, 100% { transform: rotate(-22deg) translateY(0); }
                    50% { transform: rotate(-25deg) translateY(-2px); }
                }
                @keyframes sipBlink {
                    0%, 88%, 100% { transform: scaleY(1); }
                    92%, 96% { transform: scaleY(.08); }
                }
                .sip-mascot-ringing { animation: sipRingingLean .8s ease-in-out infinite; }
                .sip-mascot-ready svg .mascota-eye {
                    transform-box: fill-box;
                    transform-origin: center;
                    animation: sipBlink 3.2s ease-in-out infinite;
                }
                .sip-mascot-on-call { animation: sipTalkBob 1.05s ease-in-out infinite; }
                .sip-mascot-on-call svg .brazo-i { animation: sipArmTalkLeft .8s ease-in-out infinite; }
                .sip-mascot-on-call svg .brazo-d { animation: sipArmTalkRight .8s ease-in-out infinite; }
                .sip-pulse-ring { animation: sipPulseRing 1.2s ease-in-out infinite; }
                .sip-call-handset { animation: sipPhoneFloat 1s ease-in-out infinite; }
                @media (prefers-reduced-motion: reduce) {
                    .sip-mascot-ringing,
                    .sip-mascot-ready svg .mascota-eye,
                    .sip-mascot-on-call,
                    .sip-mascot-on-call svg .brazo-i,
                    .sip-mascot-on-call svg .brazo-d,
                    .sip-pulse-ring,
                    .sip-call-handset {
                        animation: none !important;
                    }
                }
            `}</style>

            {open && (
                <div className="w-[19rem] sm:w-80 rounded-[2rem] bg-slate-950 p-2 shadow-2xl border border-slate-700">
                    <div className="sip-phone-shell rounded-[1.55rem] overflow-hidden text-white">
                        <div className="px-4 pt-3 pb-2 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="text-[11px] font-bold tracking-wide">
                                    {isRegistered ? `EXT ${extension}` : 'SIN EXT'}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`w-2.5 h-2.5 rounded-full ${STATUS_COLORS[status]}`} />
                                <button
                                    type="button"
                                    onClick={() => { setOpen(false); setDialContext(null); }}
                                    className="p-1 rounded-full text-slate-300 hover:text-white hover:bg-white/10 transition"
                                    title="Cerrar panel"
                                >
                                    <XMarkIcon className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        <div className="mx-3 mb-3 rounded-[1.35rem] sip-screen-glow text-slate-900 overflow-hidden">
                            <div className="px-5 pt-5 text-center">
                                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">
                                    {view.label}
                                </p>
                                <input
                                    type="text"
                                    className="mt-2 w-full text-center text-2xl font-black text-slate-950 tracking-wide bg-transparent border-none outline-none placeholder-slate-950"
                                    value={displayNumber}
                                    onChange={(e) => {
                                        if (!isInCall && showDialPad) {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                            setDialNumber(val);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        e.stopPropagation();
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleDial();
                                        }
                                    }}
                                    placeholder="Sin numero"
                                    readOnly={isInCall || !showDialPad}
                                />
                                <p className="mt-1 text-xs font-semibold text-slate-500">
                                    {showDialPad && !isInCall ? 'Digite el numero a llamar' : (isInCall ? view.caption : statusText)}
                                </p>
                                {(callClientName || dialContext?.clientName) && (
                                    <div className="mt-3 mx-auto w-11/12 text-center text-[11px] sm:text-xs font-black uppercase tracking-wide text-white bg-blue-600 shadow-md py-1.5 px-3 rounded-lg break-words leading-tight" title={callClientName || dialContext?.clientName}>
                                        {callClientName || dialContext?.clientName}
                                    </div>
                                )}
                            </div>

                            <div className="relative flex items-center justify-center pt-4 pb-3 min-h-[190px]">
                                {isInCall && (
                                    <>
                                        <div className={`absolute w-40 h-40 rounded-full border-8 ${view.ringClass} sip-pulse-ring`} />
                                    </>
                                )}

                                <div className={`sip-mascot-wrap relative z-10 ${view.mascotClass}`}>

                                    <PointMascot
                                        pose={view.mascotPose}
                                        size={122}
                                        cubreOjos={false}
                                    />

                                    {callState === 'active' && (
                                        <div className="sip-call-handset absolute right-1 top-14 w-7 h-10 rounded-[0.8rem] bg-slate-950 shadow-xl border border-slate-700">
                                            <div className="absolute left-1/2 top-1.5 h-0.5 w-3.5 -translate-x-1/2 rounded-full bg-slate-500" />
                                            <PhoneIcon className="absolute left-1/2 top-1/2 w-3.5 h-3.5 -translate-x-1/2 -translate-y-1/2 text-emerald-300" />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="px-5 pb-5">
                                {showDialPad && !isInCall && (
                                    <div className="mb-4">
                                        <div className="grid grid-cols-3 gap-2">
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((digit) => (
                                                <button
                                                    key={digit}
                                                    type="button"
                                                    onClick={() => appendDigit(String(digit))}
                                                    className={`${digit === 0 ? 'col-start-2' : ''} h-11 rounded-xl bg-white border border-slate-200 text-slate-900 text-lg font-black hover:bg-slate-100 active:scale-95 transition`}
                                                >
                                                    {digit}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 mt-3">
                                            <button
                                                type="button"
                                                onClick={removeDigit}
                                                className="h-10 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-700 text-sm font-black transition"
                                            >
                                                Borrar
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleDial}
                                                disabled={!dialNumber.trim() || !isRegistered}
                                                className="h-10 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:text-slate-500 text-white text-sm font-black transition"
                                            >
                                                Llamar
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-3 mb-4">
                                    <div className="rounded-xl bg-slate-100 border border-slate-200 px-3 py-2">
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Tiempo</p>
                                        <p className="text-xl font-black font-mono text-slate-900">
                                            {formatDuration(callSeconds)}
                                        </p>
                                    </div>
                                    <div className="rounded-xl bg-slate-100 border border-slate-200 px-3 py-2">
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Estado</p>
                                        <p className="text-sm font-black text-slate-900 truncate">
                                            {isRegistered ? 'Conectado' : 'Desconectado'}
                                        </p>
                                    </div>
                                </div>

                                {isInCall ? (
                                    <button
                                        type="button"
                                        onClick={hangup}
                                        className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-black py-3 rounded-2xl transition-colors shadow-lg shadow-red-500/25"
                                    >
                                        <PhoneXMarkIcon className="w-5 h-5" />
                                        Colgar
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleNewCall}
                                        disabled={!isRegistered}
                                        className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:text-slate-500 text-white text-sm font-black py-3 rounded-2xl transition-colors shadow-lg shadow-emerald-500/25"
                                    >
                                        <PhoneIcon className="w-5 h-5" />
                                        Nueva llamada
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div
                title={statusText}
                className="relative w-12 h-12 rounded-full bg-slate-800 shadow-xl flex items-center justify-center transition-colors cursor-default"
            >
                <PhoneIcon className="w-6 h-6 text-white" />
                <span className={`absolute top-0 right-0 w-3 h-3 rounded-full border-2 border-white ${STATUS_COLORS[status]}`} />
            </div>
        </div>
    );
}
